<script>
import { mapActions, mapState } from 'vuex'
import { required } from 'vuelidate/lib/validators'
import { cpfCnpj } from '@/utils/validator'
import AuthWrapper from '@/views/auth/components/AuthWrapper'
import Action from '@/components/Action'
import Icon from '@/components/Icon'
import InputField from '@/components/InputField'
import FormResponseMessage from '@/components/FormResponseMessage'
import { getUserErrorMessage } from '@/services/userError'

import { getTelegramBotUrl, getWhatsAppBotUrl } from '@/config/bots'

export default {
  name: 'BotAuth',

  components: {
    AuthWrapper,
    Action,
    Icon,
    InputField,
    FormResponseMessage,
  },

  data () {
    return {
      loading: false,
      success: false,
      formData: {
        user: null,
        password: null,
      },
      response: {
        message: null,
        type: null,
      },
    }
  },

  computed: {
    ...mapState('auth', ['token']),

    isAuthenticated () {
      return !!this.token
    },

    selectedChannel () {
      const channel = String(this.$route?.query?.canal || '').trim().toLowerCase()
      return channel === 'telegram' ? 'telegram' : 'whatsapp'
    },

    botUrl () {
      return this.selectedChannel === 'telegram' ? getTelegramBotUrl() : getWhatsAppBotUrl()
    },

    botAriaLabel () {
      return this.selectedChannel === 'telegram'
        ? 'Abrir bot do Telegram em nova aba'
        : 'Abrir bot do WhatsApp em nova aba'
    },
  },

  created () {
    if (this.isAuthenticated) {
      this.success = true
    }
  },

  mounted () {
    if (this.success && this.$refs.ctaBot && this.$refs.ctaBot.$el) {
      this.$refs.ctaBot.$el.focus()
    }
  },

  validations: {
    formData: {
      user: {
        required,
        cpfCnpj,
      },
      password: {
        required,
      },
    },
  },

  methods: {
    ...mapActions('auth', ['login']),

    clearForm () {
      this.formData = {
        user: null,
        password: null,
      }
    },

    clearMessage () {
      this.response = {
        message: null,
        type: null,
      }
    },

    handleSubmit () {
      this.$v.$touch()
      if (this.$v.$invalid) { return }
      this.clearMessage()
      this.loading = true
      this.login(this.formData).then(() => {
        this.success = true
        this.$nextTick(() => {
          const el = this.$refs.ctaBot && this.$refs.ctaBot.$el
          if (el) el.focus()
        })
      }).catch((response) => {
        this.loading = false
        this.clearForm()
        this.$v.formData.user.$reset()
        this.$v.formData.password.$reset()
        this.response.type = 'error'
        // Bloqueio / web não liberada: erro de conta, não de campo. Mostra só a mensagem
        // da API, sem marcar o input em vermelho — não há nada pro cliente corrigir ali.
        if (response && response.code) {
          this.response.message = getUserErrorMessage(response, 'Acesso não liberado. Entre em contato com o suporte.')
          return
        }
        if (response && response.field === 'user') {
          this.$v.formData.user.$touch()
          this.response.message = getUserErrorMessage(response, 'CPF/CNPJ não encontrado. <br/>Verifique as informações fornecidas.')
          return
        }
        if (response && response.field === 'password') {
          this.$v.formData.password.$touch()
          this.response.message = getUserErrorMessage(response, 'Senha incorreta. <br/>Verifique as informações fornecidas.')
          return
        }
        // Bloqueio (403 + code) e demais falhas: mostra a mensagem da API no próprio card.
        this.response.message = getUserErrorMessage(response, 'Não foi possível entrar. Tente novamente.')
      })
    },
  },
}
</script>

<template>
  <div>
    <div v-if="success" :class="$classes['container']" role="main">
      <action :to="{ name: 'home' }" :class="$classes['logo-link']">
        <img src="@/assets/images/logo.png" :class="$classes['logo']" alt="Scherer Autopeças">
      </action>
      <section :class="$classes['card']" role="status" aria-live="polite" aria-label="Login realizado com sucesso">
        <div :class="$classes['success-container']">
          <div :class="$classes['success-icon']" aria-hidden="true">
            <icon name="check" />
          </div>
          <h1 :class="$classes['success-title']">
            Pronto!
          </h1>
          <p :class="$classes['success-message']">
            Agora você pode utilizar o bot.
          </p>
          <action ref="ctaBot" full-width :class="$classes['card-btn']" variant="contained" color="primary"
            :url="botUrl" target="_blank" :aria-label="botAriaLabel">
            <icon v-if="selectedChannel === 'telegram'" name="telegram" />
            <icon v-else name="whatsapp" />
            Vamos para o Bot!
          </action>
        </div>
      </section>
    </div>

    <auth-wrapper v-else>
      <form @submit.prevent="handleSubmit">
        <input-field v-model="formData.user" label="CPF/CNPJ" placeholder="Escreva aqui"
          :mask="['###.###.###-##', '##.###.###/####-##']" :validation="$v.formData.user" :inputMode="'numeric'"
          :tab-index="1" />
        <input-field v-model="formData.password" label="Senha" type="password" placeholder="Escreva aqui"
          :validation="$v.formData.password" :tab-index="2" />
        <form-response-message v-if="response.message" :message="response.message" :type="response.type" />
        <div :class="$classes['form-actions']">
          <action full-width variant="contained" color="primary" type="submit" :loading="loading" :disabled="loading">
            Entrar
          </action>
        </div>
      </form>
    </auth-wrapper>
  </div>
</template>

<style module>
.container {
  position: relative;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(to bottom, #fff 20%, #ccc 100%);
  padding: 30px 20px;
}

.logo-link {
  display: contents;
}

.logo {
  position: absolute;
  top: 30px;
  height: 70px;
  width: auto;
}

.card {
  width: 100%;
  max-width: 340px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, .04), 0 4px 16px -4px rgba(0, 0, 0, .16);
  padding: 36px 40px;
}

.card-btn {
  gap: 16px;
  min-height: 44px;
}

.form-actions {
  margin-top: 25px;
}

.success-container {
  display: flex;
  gap: 10px;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: successFadeIn 0.35s ease-out;
}

@keyframes successFadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.success-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: #4caf50;
  color: #fff;
  font-size: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  animation: successIconPop 0.4s ease-out;
}

@keyframes successIconPop {
  from {
    transform: scale(0);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
}

.success-title {
  font-size: 22px;
  font-weight: 600;
  line-height: 1.25;
  margin-bottom: 0;
  color: #1a1a1a;
}

.success-message {
  font-size: 0.9375rem;
  line-height: 1.5;
  color: #555;
  margin-bottom: 28px;
}

@media (max-width: 767px) {
  .logo {
    height: 50px;
    top: 20px;
  }

  .card {
    box-shadow: none;
    padding: 24px 20px;
  }
}
</style>
